export type TranscriptionStatus = 'pending' | 'in-progress' | 'completed' | 'partial' | 'failed'
export type MediaCapturePurpose = 'walkthrough-estimate' | 'voice-chat'

export type AdminMediaCapture = {
  id: string
  tenantId: number
  purpose: MediaCapturePurpose
  customerId: number | null
  propertyId: number | null
  jobId: number | null
  filename: string
  mimeType: string
  size: number
  durationSeconds: number | null
  latitude: number | null
  longitude: number | null
  capturedAtUtc: string | null
  sessionId: string | null
  transcript: string | null
  transcriptIsPartial: boolean
  transcriptionStatus: TranscriptionStatus
  transcriptionStartedAtUtc: string | null
  transcriptionAttempts: number
  transcriptionFailureReason: string | null
  inputTokens: number | null
  outputTokens: number | null
  transcriptionCostUsd: number | null
  createdByEmail: string
  createdAtUtc: string
}

export type TranscriptSearchResponse = {
  items: AdminMediaCapture[]
  total: number
}

export type TranscriptFilters = {
  tenantId?: number
  purpose?: MediaCapturePurpose
  transcriptionStatus?: TranscriptionStatus
  createdFromUtc?: string
  createdToUtc?: string
  searchText?: string
  page: number
  pageSize: number
}

export const TRANSCRIPTS_PAGE_SIZE = 25

export const getDefaultTranscriptFilters = (): TranscriptFilters => ({
  page: 1,
  pageSize: TRANSCRIPTS_PAGE_SIZE,
})

export const buildTranscriptSearchParams = (filters: TranscriptFilters): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    page: filters.page,
    pageSize: filters.pageSize,
  }
  if (filters.tenantId !== undefined) {
    params.tenantId = filters.tenantId
  }
  if (filters.purpose) {
    params.purpose = filters.purpose
  }
  if (filters.transcriptionStatus) {
    params.transcriptionStatus = filters.transcriptionStatus
  }
  if (filters.createdFromUtc) {
    params.createdFromUtc = filters.createdFromUtc
  }
  if (filters.createdToUtc) {
    params.createdToUtc = filters.createdToUtc
  }
  if (filters.searchText?.trim()) {
    params.searchText = filters.searchText.trim()
  }
  return params
}

export const transcriptionStatusColors: Record<TranscriptionStatus, string> = {
  pending: 'gold',
  'in-progress': 'blue',
  completed: 'green',
  partial: 'orange',
  failed: 'red',
}

export const transcriptionStatusLabels: Record<TranscriptionStatus, string> = {
  pending: 'Pending',
  'in-progress': 'In progress',
  completed: 'Completed',
  partial: 'Partial',
  failed: 'Failed',
}

// Derived from the label map rather than hand-listed, so a status added to the union cannot
// reach the screen without also reaching the filter.
export const transcriptionStatusOptions = (): { value: TranscriptionStatus; label: string }[] =>
  (Object.keys(transcriptionStatusLabels) as TranscriptionStatus[]).map((status) => ({
    value: status,
    label: transcriptionStatusLabels[status],
  }))

// The server owns this rule and now enforces it, refusing a retry of any capture that already
// holds a transcript — complete or partial — and returning a 400 that explains why. This
// mirrors it so the button can be hidden without a round trip. Listed as exclusions rather
// than an allow-list on purpose: an unknown status stays retryable and lets the server
// adjudicate, which matters in the one admin tool meant for recovering stuck captures.
// The honest fix is a `canRetry` flag on the payload — SYN-4058.
export const canRetryTranscription = (capture: Pick<AdminMediaCapture, 'transcriptionStatus'>): boolean =>
  capture.transcriptionStatus !== 'completed' &&
  capture.transcriptionStatus !== 'in-progress' &&
  capture.transcriptionStatus !== 'partial'

export const isPartialTranscript = (capture: Pick<AdminMediaCapture, 'transcriptionStatus'>): boolean =>
  capture.transcriptionStatus === 'partial'

// Lives next to the rule it explains so the two cannot be updated independently.
export const PARTIAL_TRANSCRIPT_EXPLANATION =
  "Transcription stopped at the provider's output limit, so the end of the recording is missing. "
  + 'Retrying would stop at the same point, so it cannot be retried until chunked transcription ships.'

export const purposeLabels: Record<MediaCapturePurpose, string> = {
  'walkthrough-estimate': 'Walkthrough',
  'voice-chat': 'Voice chat',
}

export const formatTokens = (inputTokens: number | null, outputTokens: number | null): string => {
  if (inputTokens === null || outputTokens === null) {
    return '—'
  }
  return `${inputTokens.toLocaleString()} / ${outputTokens.toLocaleString()}`
}

export const formatCostUsd = (costUsd: number | null): string => {
  if (costUsd === null) {
    return '—'
  }
  return `$${costUsd.toFixed(4)}`
}

export const isVideoCapture = (mimeType: string): boolean => mimeType.startsWith('video/')

// Shown in place of the transcript. A completed transcription with nothing in it means the
// recording held no speech, which is neither a failure nor still-pending work.
export const transcriptPlaceholder = (
  capture: Pick<AdminMediaCapture, 'transcriptionStatus' | 'transcript'>
): string => {
  switch (capture.transcriptionStatus) {
    case 'completed':
    case 'partial':
      return 'No speech detected in this recording.'
    case 'failed':
      return 'Transcription failed.'
    case 'in-progress':
      return 'Transcription in progress.'
    case 'pending':
      return 'Transcription pending.'
    default:
      return 'Transcription status unknown.'
  }
}

export const formatDurationSeconds = (seconds: number | null): string => {
  if (seconds === null || seconds < 0) {
    return '—'
  }
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  return `${minutes}:${String(remainder).padStart(2, '0')}`
}
